
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CopyIcon, Share2, Youtube, Globe, Eye } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

interface LiveStreamingSetupProps {
  isLiveStreaming: boolean;
  onToggleLiveStreaming: (platform?: string) => void;
}

const LiveStreamingSetup: React.FC<LiveStreamingSetupProps> = ({ 
  isLiveStreaming, 
  onToggleLiveStreaming 
}) => {
  const { toast } = useToast();
  const [platform, setPlatform] = useState<"public" | "youtube">("public");
  const [youtubeKey, setYoutubeKey] = useState("");
  const [title, setTitle] = useState("Annual Tech Conference - Live Stream");
  const [description, setDescription] = useState("Join us for the latest trends and innovations in technology.");
  
  const publicPageUrl = "https://event.cecurestream.com/public/evt-1";
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicPageUrl);
    toast({
      title: "Link copied",
      description: "Public page URL copied to clipboard",
    });
  };
  
  const handleStartStreaming = () => {
    onToggleLiveStreaming(platform);
  };
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Live Streaming</CardTitle>
        <CardDescription>
          Stream this event to a public page or other platforms
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLiveStreaming ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-md p-3 flex items-center">
              <div className="bg-green-100 p-1 rounded-full mr-3">
                <Eye className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-green-800">Live Now</h4>
                <p className="text-xs text-green-700">Your event is being streamed live</p>
              </div>
            </div>
            
            <div className="flex flex-col space-y-2">
              <Label htmlFor="public-url">Public page URL</Label>
              <div className="flex">
                <Input id="public-url" value={publicPageUrl} readOnly className="flex-grow" />
                <Button variant="outline" className="ml-2 flex-shrink-0" onClick={handleCopyLink}>
                  <CopyIcon className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500">Anyone with this link can view the livestream</p>
            </div>
            
            <div className="flex justify-end">
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={() => onToggleLiveStreaming()}
              >
                End Livestream
              </Button>
            </div>
          </div>
        ) : (
          <Tabs defaultValue="public" onValueChange={(value) => setPlatform(value as "public" | "youtube")}>
            <TabsList className="grid grid-cols-2 mb-4">
              <TabsTrigger value="public">
                <Globe className="h-4 w-4 mr-2" />
                Public Page
              </TabsTrigger>
              <TabsTrigger value="youtube">
                <Youtube className="h-4 w-4 mr-2" />
                YouTube
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="public" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="stream-title">Stream Title</Label>
                <Input 
                  id="stream-title" 
                  value={title} 
                  onChange={(e) => setTitle(e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="stream-description">Description</Label>
                <Input 
                  id="stream-description" 
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)} 
                />
              </div>
            </TabsContent>
            
            <TabsContent value="youtube" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="youtube-key">YouTube Stream Key</Label>
                <Input 
                  id="youtube-key" 
                  value={youtubeKey} 
                  onChange={(e) => setYoutubeKey(e.target.value)} 
                  type="password"
                />
                <p className="text-xs text-gray-500">
                  You can find your stream key in your YouTube Studio Live Dashboard
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="yt-stream-title">Stream Title</Label>
                <Input 
                  id="yt-stream-title" 
                  value={title} 
                  onChange={(e) => setTitle(e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="yt-stream-description">Description</Label>
                <Input 
                  id="yt-stream-description" 
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)} 
                />
              </div>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
      {!isLiveStreaming && (
        <CardFooter>
          <Button 
            className="w-full bg-cecure-700 hover:bg-cecure-800"
            onClick={handleStartStreaming}
            disabled={platform === "youtube" && !youtubeKey}
          >
            <Share2 className="h-4 w-4 mr-2" />
            Start Livestream
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default LiveStreamingSetup;
